import type { IHour } from "@gettactic/api";
import { useQueries } from "@tanstack/react-query";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

/**
 * Office-level hour rules plus the given resource's own rules, merged and
 * deduped by id. Both are requested so the result is the same whether or not
 * the backend folds office rules into the per-resource listing. The keys share
 * the `["hours.rules", officeId]` prefix the settings screens invalidate.
 */
export function useResourceHourRules(
  officeId: string | null,
  resourceId: string | null
): { rules: IHour[]; isPending: boolean } {
  const { api } = useTacticHooks();

  return useQueries({
    queries: [
      {
        queryKey: ["hours.rules", officeId, "office"],
        queryFn: async () => {
          const res = await api.client.hours.rules(officeId ?? "");
          if (!res.ok) {
            throw new Error("Failed to load office hour rules");
          }
          return res.result?.elements ?? [];
        },
        enabled: !!officeId
      },
      {
        queryKey: ["hours.rules", officeId, resourceId],
        queryFn: async () => {
          const res = await api.client.hours.rules(
            officeId ?? "",
            resourceId ?? undefined
          );
          if (!res.ok) {
            throw new Error("Failed to load resource hour rules");
          }
          return res.result?.elements ?? [];
        },
        enabled: !!officeId && !!resourceId
      }
    ],
    combine: (results) => {
      const byId = new Map<string, IHour>();
      for (const result of results) {
        for (const rule of result.data ?? []) {
          byId.set(rule.id, rule);
        }
      }
      return {
        rules: [...byId.values()],
        isPending: results.some((result) => result.isPending)
      };
    }
  });
}
